export type CommitteeStatus = "ACTIVE" | "INACTIVE";
export type CommitteeCatalogTab = "all" | "active";
export type CommitteeAssignmentStatus = "ACTIVE" | "INACTIVE";
export type CommitteeSortField =
  | "NAME"
  | "STATUS"
  | "CORE"
  | "ACTIVE_PARTICIPANT_COUNT"
  | "INACTIVE_PARTICIPANT_COUNT"
  | "TOTAL_PARTICIPANT_COUNT"
  | "CREATED_AT"
  | "UPDATED_AT";
export type CommitteeAssignmentSortField = "START_DATE" | "END_DATE" | "STATUS" | "CREATED_AT";
export type SortDirection = "ASC" | "DESC";

export type CommitteeOrderByInput = {
  field: CommitteeSortField;
  direction: SortDirection;
};

export type CommitteeAssignmentOrderByInput = {
  field: CommitteeAssignmentSortField;
  direction: SortDirection;
};

export const DEFAULT_COMMITTEE_ASSIGNMENT_ORDER_BY: CommitteeAssignmentOrderByInput = {
  field: "END_DATE",
  direction: "DESC"
};

export type CommitteesInput = {
  search?: string;
  sectionId?: string;
  status?: CommitteeStatus;
  core?: boolean;
};

export type CommitteeNode = {
  id: string;
  name: string;
  status: CommitteeStatus;
  core: boolean;
  activeParticipantCount: number;
  inactiveParticipantCount: number;
  totalParticipantCount: number;
};

export type CommitteeRow = CommitteeNode;

export type CommitteeAssignmentNode = {
  id: string;
  startDate: string | null;
  endDate: string | null;
  status: CommitteeAssignmentStatus;
  title: {
    id: string;
    title: string;
  };
  person: {
    id: string;
    pgaId: string | null;
    displayName: string | null;
    profilePhoto: string | null;
  } | null;
};

export type CommitteeAssignmentWithCommittee = CommitteeAssignmentNode & {
  committeeName: string;
};

export type CommitteeAssignmentTitle = {
  id: string;
  title: string;
};

export type CommitteeAssignmentTitlesQueryResponse = {
  committeeAssignmentTitles: CommitteeAssignmentTitle[];
};

export type BackofficeCommitteesQueryResponse = {
  backofficeCommittees: {
    nodes: CommitteeNode[];
    totalCount: number;
    pageInfo: {
      hasNextPage: boolean;
      hasPreviousPage: boolean;
      startCursor?: string;
      endCursor?: string;
    };
  };
};

export type BackofficeCommitteesQueryVariables = {
  first?: number;
  last?: number;
  before?: string;
  after?: string;
  input?: CommitteesInput;
  orderBy?: CommitteeOrderByInput;
};

export type BackofficeCommitteeAssignmentsQueryResponse = {
  backofficeCommittee: {
    id: string;
    assignments: {
      nodes: CommitteeAssignmentNode[];
      totalCount: number;
      pageInfo: {
        hasNextPage: boolean;
        hasPreviousPage: boolean;
        startCursor?: string;
        endCursor?: string;
      };
    };
  } | null;
};

export type BackofficeCommitteeAssignmentsQueryVariables = {
  id: string;
  first?: number;
  last?: number;
  before?: string;
  after?: string;
  status?: CommitteeAssignmentStatus;
  search?: string;
  orderBy?: CommitteeAssignmentOrderByInput;
};

export type CommitteeParticipantEmailNode = {
  id: string;
  title?: {
    id: string;
  } | null;
  person: {
    id: string;
    email: string | null;
  } | null;
};

export type BackofficeCommitteeParticipantEmailsQueryResponse = {
  backofficeCommittee: {
    id: string;
    assignments: {
      nodes: CommitteeParticipantEmailNode[];
      pageInfo: {
        hasNextPage: boolean;
        endCursor?: string | null;
      };
    };
  } | null;
};

export type BackofficeCommitteeParticipantEmailsQueryVariables = {
  id: string;
  first?: number;
  after?: string;
  status?: CommitteeAssignmentStatus;
  orderBy?: CommitteeAssignmentOrderByInput;
};

export type CommitteeDetailNode = CommitteeNode & {
  section: { id: string };
};

export type BackofficeCommitteeQueryResponse = {
  backofficeCommittee: CommitteeDetailNode | null;
};

export type BackofficeCommitteeQueryVariables = {
  id: string;
};

export type CrmSectionOption = {
  id: string;
  name: string;
  legacyId: string | null;
};

export type CrmSectionsForGroupEmailQueryResponse = {
  crmSections: {
    nodes: CrmSectionOption[];
    pageInfo: {
      hasNextPage: boolean;
      endCursor?: string | null;
    };
  };
};

export type CrmSectionsForGroupEmailQueryVariables = {
  first: number;
  after?: string;
};

export type CommitteeParticipationFilter = "all" | "active" | "inactive";
